import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Wrench } from 'lucide-react';

/** Phone and tablet adaptation of the supplied card-hand gallery using existing expertise content. */
export default function ExpertiseFan({ categories, icons }) {
  const [selected, setSelected] = useState(0);
  const handRef = useRef(null);
  const [handWidth, setHandWidth] = useState(280);
  const [cardWidth, setCardWidth] = useState(120);
  useEffect(() => {
    const node = handRef.current;
    if (!node) return;
    const measure = () => {
      setHandWidth(node.getBoundingClientRect().width);
      setCardWidth(parseFloat(getComputedStyle(node).getPropertyValue('--expertise-fan-card-width')) || 120);
    };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(node);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, [categories.length]);
  const isTabletSize = cardWidth > 120;
  const spacing = Math.max(12, Math.min(isTabletSize ? 85 : 25, (handWidth - (isTabletSize ? cardWidth * 1.6 : 180)) / Math.max(1, categories.length - 1)));
  const active = categories[selected] || categories[0];
  if (!active) return null;
  const ActiveIcon = icons[active.id] || Wrench;
  const select = index => setSelected((index + categories.length) % categories.length);

  return (
    <div className="expertise-fan">
      <div className="expertise-fan-detail" id="expertise-fan-detail" aria-live="polite" aria-atomic="true">
        <motion.article className="expertise-card" key={active.id} initial={{ opacity: .6, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .2 }}>
          <div className="expertise-card-header">
            <div className="expertise-icon"><ActiveIcon size={24} aria-hidden="true" /></div>
            <h3>{active.title}</h3>
          </div>
          <div className="expertise-tags">
            {active.skills.map(skill => <span className="expertise-tag" key={skill}>{skill}</span>)}
          </div>
        </motion.article>
      </div>
      <div className="expertise-fan-hand" ref={handRef} role="group" aria-label="Choose an expertise category">
        {categories.map((category, index) => {
          const offset = index - (categories.length - 1) / 2;
          const Icon = icons[category.id] || Wrench;
          const isActive = category.id === active.id;
          return (
            <motion.button type="button" className="expertise-fan-card" key={category.id}
              aria-label={category.title} aria-pressed={isActive} aria-controls="expertise-fan-detail"
              onClick={() => select(index)}
              animate={{ x: offset * spacing, y: Math.abs(offset) * (isTabletSize ? 14 : 7) - (isActive ? (isTabletSize ? 76 : 44) : 0), rotate: offset * 7, scale: isActive ? 1 : .93 }}
              transition={{ type: 'spring', stiffness: 180, damping: 24 }}
              style={{ zIndex: isActive ? 20 : index + 1 }}>
              <span className="expertise-fan-number">{String(index + 1).padStart(2, '0')}</span>
              <Icon size={26} aria-hidden="true" />
              <span className="expertise-fan-title">{category.title}</span>
              {isTabletSize && <span className="expertise-tags">
                {category.skills.map(skill => <span className="expertise-tag" key={skill}>{skill}</span>)}
              </span>}
            </motion.button>
          );
        })}
      </div>
      <div className="expertise-fan-controls">
        <button type="button" onClick={() => select(selected - 1)} aria-label="Previous expertise category"><ChevronLeft size={20} /></button>
        <span aria-hidden="true">{String(selected + 1).padStart(2, '0')} / {String(categories.length).padStart(2, '0')}</span>
        <button type="button" onClick={() => select(selected + 1)} aria-label="Next expertise category"><ChevronRight size={20} /></button>
      </div>
      <p className="expertise-carousel-help">Tap a card to explore its skills</p>
    </div>
  );
}
